/**
 * TikTok Business Account OAuth helpers.
 *
 * Handles building the authorize URL, stashing the session across the
 * full-page redirect to TikTok, and consuming the return path after
 * the callback page finishes exchanging the code.
 */

export const TIKTOK_BUSINESS_OAUTH_SCOPE =
	"user.info.basic,user.info.username,user.info.stats,user.info.profile,user.account.type,user.insights,video.list,video.insights,comment.list,discovery.search.words,biz.creator.info,biz.creator.insights,tto.campaign.link,biz.spark.auth";

export const TIKTOK_BUSINESS_LINK_RETURN_KEY = "tiktok_business_link_return";
export const TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY = "tiktok_business_link_reopen_step";
export const TIKTOK_BUSINESS_LINK_ACCESS_TOKEN_KEY = "tiktok_business_link_access_token";
export const TIKTOK_BUSINESS_LINK_ERROR_KEY = "tiktok_business_link_error";
export const TIKTOK_BUSINESS_LINK_STATE_KEY = "tiktok_business_link_state";

export function getTiktokBusinessRedirectUri(): string {
	const configured = (import.meta.env.VITE_TIKTOK_BUSINESS_REDIRECT_URL as string | undefined)?.trim();
	if (configured) return configured.replace(/\/$/, "");
	return `${window.location.origin}/tiktok-business-link-auth`;
}

export function buildTiktokBusinessAuthorizeUrl(params: {
	clientKey: string;
	redirectUri: string;
	state?: string;
	scope?: string;
}): string {
	const query = new URLSearchParams();
	query.set("client_key", params.clientKey);
	query.set("scope", params.scope || TIKTOK_BUSINESS_OAUTH_SCOPE);
	query.set("response_type", "code");
	query.set("redirect_uri", params.redirectUri.replace(/\/$/, ""));
	if (params.state) query.set("state", params.state);
	return `https://www.tiktok.com/v2/auth/authorize?${query.toString()}`;
}

export function stashTiktokBusinessLinkReturn(returnPath: string, reopenStep?: string): void {
	const safePath = returnPath.startsWith("/") && !returnPath.startsWith("//") ? returnPath : "/campaigns";
	sessionStorage.setItem(TIKTOK_BUSINESS_LINK_RETURN_KEY, safePath);
	if (reopenStep) {
		sessionStorage.setItem(TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY, reopenStep);
	} else {
		sessionStorage.removeItem(TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY);
	}
}

export function stashTiktokBusinessLinkAccessToken(accessToken: string | null | undefined): void {
	if (accessToken) {
		sessionStorage.setItem(TIKTOK_BUSINESS_LINK_ACCESS_TOKEN_KEY, accessToken);
	} else {
		sessionStorage.removeItem(TIKTOK_BUSINESS_LINK_ACCESS_TOKEN_KEY);
	}
}

export function consumeTiktokBusinessLinkReturn(): { returnPath: string; reopenStep?: string } {
	const returnPath = sessionStorage.getItem(TIKTOK_BUSINESS_LINK_RETURN_KEY) || "/campaigns";
	const reopenStep = sessionStorage.getItem(TIKTOK_BUSINESS_LINK_REOPEN_STEP_KEY) || undefined;
	sessionStorage.removeItem(TIKTOK_BUSINESS_LINK_RETURN_KEY);
	const safePath = returnPath.startsWith("/") && !returnPath.startsWith("//") ? returnPath : "/campaigns";
	return { returnPath: safePath, reopenStep };
}

/**
 * Start TikTok Business Account OAuth: get signed state from backend,
 * stash session, then redirect to TikTok.
 */
export async function startTiktokBusinessLinkOAuth(options?: {
	returnPath?: string;
	reopenStep?: string;
}): Promise<void> {
	const clientKey = import.meta.env.VITE_TIKTOK_BUSINESS_CLIENT_KEY as string | undefined;
	if (!clientKey?.trim()) {
		throw new Error("TikTok Business app is not configured (VITE_TIKTOK_BUSINESS_CLIENT_KEY).");
	}

	const { authenticationService } = await import("@/api/authentication");
	const { tiktokCreatorLinkApi } = await import("@/api/tiktokCreatorLink");

	const { state } = await tiktokCreatorLinkApi.startLink();
	sessionStorage.setItem(TIKTOK_BUSINESS_LINK_STATE_KEY, state);

	stashTiktokBusinessLinkAccessToken(authenticationService.getAccessToken());
	stashTiktokBusinessLinkReturn(
		options?.returnPath || `${window.location.pathname}${window.location.search}`,
		options?.reopenStep,
	);

	const redirectUri = getTiktokBusinessRedirectUri();
	window.location.href = buildTiktokBusinessAuthorizeUrl({
		clientKey: clientKey.trim(),
		redirectUri,
		state,
	});
}
